/**
 * TeamWorkloadSection Component
 *
 * Card displaying team workload with member rows and carousel pagination.
 * Shows task distribution per member with segmented bars and count badges.
 *
 * @module pages/DashboardPage/components/TeamWorkloadSection
 */

import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination, Skeleton } from '@mantine/core';
import { IconClipboardList, IconUsers } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import { EmptyState } from '@/components/common';

import { TeamMemberRow } from '../TeamMemberRow';
import { MEMBERS_PER_PAGE } from './TeamWorkloadSection.utils';
import type { TeamWorkloadSectionProps } from './TeamWorkloadSection.types';
import styles from './TeamWorkloadSection.module.css';

const SKELETON_ROW_COUNT = 4;

/**
 * TeamWorkloadSection renders a card with team member workload rows.
 * Each row contains an avatar, segmented distribution bar, and count badges.
 * Supports carousel pagination when members exceed MEMBERS_PER_PAGE.
 */
export const TeamWorkloadSection = memo(function TeamWorkloadSection({
  members,
  isLoading,
}: TeamWorkloadSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();

  const totalPages = Math.ceil(members.length / MEMBERS_PER_PAGE);

  const pageMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * MEMBERS_PER_PAGE;
    return members.slice(startIndex, startIndex + MEMBERS_PER_PAGE);
  }, [members, currentPage]);

  const maxTasks = useMemo(
    () =>
      members.reduce(
        (max, member) =>
          Math.max(max, member.done + member.active + member.overdue),
        0
      ),
    [members]
  );

  const showSkeleton = isLoading && members.length === 0;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.title}>
          <IconUsers size={ICON_SIZE_SM} />
          <span>{t('dashboard.teamWorkload.title')}</span>
        </div>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendBlue}`} />
            {t('dashboard.teamWorkload.legend.active')}
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendRed}`} />
            {t('dashboard.teamWorkload.legend.overdue')}
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendGreen}`} />
            {t('dashboard.teamWorkload.legend.done')}
          </span>
        </div>
      </div>
      {showSkeleton ? (
        <div className={styles.content}>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, idx) => (
            <div key={idx} className={styles.skeletonRow}>
              <Skeleton height={28} width={28} circle />
              <Skeleton height={12} width={80} radius="sm" />
              <div className={styles.skeletonBar}>
                <Skeleton height={8} width="100%" radius="md" />
              </div>
              <div className={styles.skeletonBadges}>
                <Skeleton height={20} width={24} radius="md" />
                <Skeleton height={20} width={24} radius="md" />
                <Skeleton height={20} width={24} radius="md" />
              </div>
            </div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('dashboard.emptyState.teamWorkload')}
          icon={<IconClipboardList size={32} />}
        />
      ) : (
        <>
          <div className={styles.content}>
            {pageMembers.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                maxTasks={maxTasks}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
                size="sm"
              />
            </div>
          )}
        </>
      )}
    </section>
  );
});

TeamWorkloadSection.displayName = 'TeamWorkloadSection';
